import apiClient from "@/lib/apiClient";

export enum AccountRole {
  ADMIN = "ADMIN",
  MEMBER = "MEMBER",
}

export interface Account {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  created_at: string;
  updated_at: string;
}

export interface AccountSettings {
  id: string;
  account_id: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  settings?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface UserWithRoles {
  user_id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  avatar_id?: number;
  roles: AccountRole[];
}

export interface AccountWithUsers extends Account {
  users: UserWithRoles[];
}

export interface AccountWithWorkspaces extends Account {
  workspaces: Array<{
    id: string;
    name: string;
    slug: string;
    domain?: string;
  }>;
}

class AccountService {
  // Basic CRUD Operations
  async createAccount(data: { name: string; domain?: string; slug?: string }): Promise<Account> {
    return apiClient.post("/accounts", data);
  }

  async getAccount(accountId: string): Promise<Account> {
    return apiClient.get(`/accounts/${accountId}`);
  }

  async getUserAccounts(userId: string): Promise<Account[]> {
    return apiClient.get(`/users/${userId}/accounts`);
  }

  async getAccountBySlug(slug: string): Promise<Account> {
    return apiClient.get(`/accounts/slug/${slug}`);
  }

  async updateAccount(
    accountId: string,
    data: { name?: string; slug?: string; domain?: string }
  ): Promise<Account> {
    return apiClient.put(`/accounts/${accountId}`, data);
  }

  async deleteAccount(accountId: string): Promise<void> {
    return apiClient.delete(`/accounts/${accountId}`);
  }

  // Workspace Management
  async getAccountWorkspaces(accountId: string): Promise<AccountWithWorkspaces> {
    return apiClient.get(`/accounts/${accountId}/workspaces`);
  }

  // User Management
  async addUserToAccount(accountId: string, userId: string): Promise<void> {
    return apiClient.post(`/accounts/${accountId}/users`, { user_id: userId });
  }

  async removeUserFromAccount(accountId: string, userId: string): Promise<void> {
    return apiClient.delete(`/accounts/${accountId}/users/${userId}`);
  }

  async getAccountUsers(accountId: string): Promise<AccountWithUsers> {
    return apiClient.get(`/accounts/${accountId}/users`);
  }

  // Role Management
  async assignAccountRole(accountId: string, userId: string, role: AccountRole): Promise<void> {
    return apiClient.post(`/accounts/${accountId}/roles`, { user_id: userId, role });
  }

  async removeAccountRole(accountId: string, userId: string, role: AccountRole): Promise<void> {
    // NOTE (pre-existing, not fixed here — type-only pass): apiClient.delete()
    // has no request-body support (unlike post/put/patch), so `{ user_id, role }`
    // was previously being spread onto the fetch RequestInit as inert extra
    // keys — never actually sent. The backend's DELETE /accounts/{id}/roles
    // reads a required JSON body (RemoveAccountRoleRequest), so this call
    // likely 422s today regardless of what's passed here. Dropping the
    // no-op argument to fix the type error without changing behavior;
    // the real fix is adding body support to apiClient.delete().
    return apiClient.delete(`/accounts/${accountId}/roles`);
  }

  // Account Settings
  async getAccountSettings(accountId: string): Promise<AccountSettings> {
    return apiClient.get(`/accounts/${accountId}/settings`);
  }

  async updateAccountSettings(
    accountId: string,
    settings: {
      logo_url?: string;
      primary_color?: string;
      secondary_color?: string;
      settings?: Record<string, any>;
    }
  ): Promise<AccountSettings> {
    return apiClient.put(`/accounts/${accountId}/settings`, settings);
  }
}

export default new AccountService();

// For backward compatibility
export const accountService = new AccountService();
